import type {
  ApiErrorPayload,
  ConfigResponse,
  OnsiteSaleRequest,
  OnsiteSaleResult,
  OnsiteVariantLookup,
  OrdersResponse,
} from "../shared/domain";

export class ApiError extends Error {
  readonly payload: ApiErrorPayload;
  readonly status: number;

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = "ApiError";
    this.payload = payload;
    this.status = status;
  }
}

interface AuthLoginResponse {
  token: string;
  idleTimeoutMs: number;
  lastApprovedAt: number;
}

interface AuthSessionState extends AuthLoginResponse {}

const AUTH_SESSION_KEY = "natalify-auth-session-v1";
const MAX_TIMER_DELAY_MS = 2_147_000_000;
const authListeners = new Set<() => void>();
let authSession: AuthSessionState | null = restoreAuthSession();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function restoreAuthSession(): AuthSessionState | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (!stored) return null;
    const candidate: unknown = JSON.parse(stored);
    if (!candidate || typeof candidate !== "object") return null;
    const value = candidate as Record<string, unknown>;
    if (typeof value.token !== "string" || !/^[a-f\d]{64}$/i.test(value.token)) return null;
    if (typeof value.idleTimeoutMs !== "number" || !Number.isFinite(value.idleTimeoutMs) || value.idleTimeoutMs <= 0) return null;
    if (typeof value.lastApprovedAt !== "number" || !Number.isFinite(value.lastApprovedAt)) return null;
    return { token: value.token, idleTimeoutMs: value.idleTimeoutMs, lastApprovedAt: value.lastApprovedAt };
  } catch {
    return null;
  }
}

function saveAuthSession(): void {
  if (!authSession || typeof window === "undefined") return;
  try { sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authSession)); } catch { /* The in-memory session remains usable if storage is unavailable. */ }
}

function notifyAuthListeners(): void {
  for (const listener of authListeners) listener();
}

function clearAuthSession(): void {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  authSession = null;
  if (typeof window !== "undefined") {
    try { sessionStorage.removeItem(AUTH_SESSION_KEY); } catch { /* Session storage is optional. */ }
  }
  notifyAuthListeners();
}

function scheduleAuthExpiry(): void {
  if (expiryTimer) clearTimeout(expiryTimer);
  if (!authSession) return;
  const remainingMs = authSession.lastApprovedAt + authSession.idleTimeoutMs - Date.now();
  if (remainingMs <= 0) {
    clearAuthSession();
    return;
  }
  expiryTimer = setTimeout(() => {
    if (!authSession) return;
    if (Date.now() >= authSession.lastApprovedAt + authSession.idleTimeoutMs) clearAuthSession();
    else scheduleAuthExpiry();
  }, Math.min(remainingMs, MAX_TIMER_DELAY_MS));
}

function setAuthSession(session: AuthSessionState): void {
  authSession = session;
  saveAuthSession();
  scheduleAuthExpiry();
  notifyAuthListeners();
}

if (authSession) scheduleAuthExpiry();

export function subscribeToAuthState(listener: () => void): () => void {
  authListeners.add(listener);
  return () => { authListeners.delete(listener); };
}

function syncAuthActivity(response: Response): void {
  if (!authSession) return;
  const approvedAt = Number(response.headers.get("x-natalify-auth-last-approved-at"));
  if (!Number.isFinite(approvedAt) || approvedAt <= authSession.lastApprovedAt) return;
  const timeout = Number(response.headers.get("x-natalify-auth-idle-timeout-ms"));
  if (Number.isFinite(timeout) && timeout > 0) authSession.idleTimeoutMs = timeout;
  authSession.lastApprovedAt = approvedAt;
  saveAuthSession();
  scheduleAuthExpiry();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    const headers = new Headers(init?.headers);
    if (!headers.has("content-type")) headers.set("content-type", "application/json");
    if (path !== "/api/auth/login" && authSession) headers.set("authorization", `Bearer ${authSession.token}`);
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError({ category: "unavailable", code: "NETWORK_UNAVAILABLE", message: "Natalify could not reach the local server. Check that the server is running, then retry.", retryable: true }, 0);
  }

  if (path !== "/api/auth/login") syncAuthActivity(response);
  const contentType = response.headers.get("content-type") ?? "";
  const data: unknown = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const payload = isApiErrorPayload(data) ? data.error : { category: "unavailable" as const, code: "HTTP_ERROR", message: "The operation failed. Retry from the current screen.", retryable: true };
    if (path !== "/api/auth/login" && (payload.code === "AUTH_REQUIRED" || payload.code === "AUTH_SESSION_EXPIRED")) clearAuthSession();
    throw new ApiError(payload, response.status);
  }

  return data as T;
}

function isApiErrorPayload(value: unknown): value is { error: ApiErrorPayload } {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  const error = candidate.error;
  return !!error && typeof error === "object" && typeof (error as Record<string, unknown>).message === "string";
}

export const api = {
  isAuthenticated: () => {
    if (authSession && Date.now() >= authSession.lastApprovedAt + authSession.idleTimeoutMs) clearAuthSession();
    return Boolean(authSession);
  },
  login: async (pin: string) => {
    const session = await request<AuthLoginResponse>("/api/auth/login", { method: "POST", body: JSON.stringify({ pin }) });
    setAuthSession(session);
    return session;
  },
  getConfig: () => request<ConfigResponse>("/api/config"),
  getOrders: () => request<OrdersResponse>("/api/orders"),
  lookupOnsiteVariant: (barcode: string) => request<OnsiteVariantLookup>("/api/onsite/variant", { method: "POST", body: JSON.stringify({ barcode }) }),
  createOnsiteSale: (input: OnsiteSaleRequest) => request<{ status: "success"; sale: OnsiteSaleResult }>("/api/onsite/sale", { method: "POST", body: JSON.stringify(input) }),
  reconcileOnsiteSale: (operationKey: string) => request<{ status: "success"; sale: OnsiteSaleResult } | { status: "unresolved"; operationKey: string; message: string }>("/api/onsite/sale/reconcile", { method: "POST", body: JSON.stringify({ operationKey }) }),
  fulfill: (input: { orderId: string; unitIds: string[]; orderVersion: number; operationKey: string }) => request<{ status: "success"; fulfillment: { orderId: string; orderNumber: string; fulfilledAt: string; operationKey: string } }>(`/api/orders/${encodeURIComponent(input.orderId)}/fulfill`, { method: "POST", body: JSON.stringify(input) }),
  reconcileFulfillment: (orderId: string, operationKey: string) => request<{ status: "success"; fulfillment: { orderId: string; orderNumber: string; fulfilledAt: string; operationKey: string } } | { status: "unresolved"; operationKey: string; message: string }>(`/api/orders/${encodeURIComponent(orderId)}/fulfill/reconcile`, { method: "POST", body: JSON.stringify({ operationKey }) }),
  setScenario: (scenario: string) => request<{ status: "success"; scenario: string }>("/api/mock/scenario", { method: "POST", body: JSON.stringify({ scenario }) }),
  resetScenario: () => request<{ status: "success"; scenario: string }>("/api/mock/reset", { method: "POST", body: "{}" }),
};
